import { useEffect, useRef, useState } from "react";
import L, { LatLngExpression, marker } from "leaflet";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  Polyline,
  LayerGroup,
} from "react-leaflet";

import { DriverWithTracking } from "../../models/DriverTracking";
import getLatestPoint from "./../../utils/getLatestPoint";
import { set } from "react-hook-form";
import useSingleSetting from "../../data/useSingleSetting";

export default function TrackingMap({ driverTracking }: { driverTracking: DriverWithTracking[] }) {
  const [icon, setIcon] = useState<any>(null);
  const [markers, setMarkers] = useState<any[]>([]);
  const [polylines, setPolylines] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { singleSetting: longSetting } = useSingleSetting("MapCentre:Long");
  const { singleSetting: latSetting } = useSingleSetting("MapCentre:Lat");
  const [mapCentreLong, setMapCentreLong] = useState<number>(51.049758);
  const [mapCentreLat, setMapCentreLat] = useState<number>(-2.407408);
  const [coordinatesLoading, setCoordinatesLoading] = useState(true);

  const markerRef = useRef(L.layerGroup());

  const myCustomColour = "#224b96";

  // const markerHtmlStyles = `
  //     background-color: ${myCustomColour};
  //     width: 2.5rem;
  //     height: 2.5rem;
  //     display: block;
  //     left: -1rem;
  //     top: -1rem;
  //     position: relative;
  //     border-radius: 3rem 3rem 0;
  //     transform: rotate(45deg);
  //     border: 1px solid #FFFFFF;`;

  const getCustomIcon = (colour: string) => {
    return L.divIcon({
      className: "my-custom-pin",
      iconSize: [32, 32],
      // iconAnchor: [18, 36],
      // labelAnchor: [-6, 0],
      popupAnchor: [0, -10],
      html: `<span class="driver-tracker" style="background-color: ${colour};"/>`, //style="${markerHtmlStyles} />`
    });
  };

  useEffect(() => {
    const markers = driverTracking
      .filter((x) => x.show)
      .map((driver) => {
        const recentPoint = getLatestPoint(driver);
        return (
          recentPoint &&
          recentPoint?.latitude! &&
          recentPoint?.longitude! && (
            <Marker
              key={recentPoint.latitude + recentPoint.longitude}
              position={[recentPoint?.latitude!, recentPoint?.longitude!]}
              icon={getCustomIcon(driver.colour)}
            >
              <Popup className="w-[12rem]">
                <div>
                  {driver.driver.forename} {driver.driver.surname}
                </div>
              </Popup>
            </Marker>
          )
        );
      });

    setMarkers(markers);

    setPolylines(
      driverTracking
        .filter((x) => x.show)
        .map((driver, i) => {
          var points = driver.tracking
            .sort(
              (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
            )
            .map((point) => [
              point.latitude,
              point.longitude,
            ]) as LatLngExpression[];
          return (
            <Polyline
              key={i}
              pathOptions={{ color: driver.colour }}
              positions={points}
            />
          );
        })
    );

    setIcon(getCustomIcon(myCustomColour));
    setIsLoading(false);
  }, [driverTracking]);

  useEffect(() => {
    if (longSetting.isLoading || latSetting.isLoading) return;
    // A missing/404'd setting resolves as { data: null }, so guard the inner payload too.
    if (longSetting.data?.data?.value == null) return;
    if (latSetting.data?.data?.value == null) return;

    setMapCentreLong(+longSetting.data.data.value);
    setMapCentreLat(+latSetting.data.data.value);

    setCoordinatesLoading(false);

  }, [longSetting.isLoading, latSetting.isLoading])

  return (
    <div className="h-[65vh] z-10 w-full">
      {!isLoading && !coordinatesLoading && (
        <MapContainer
          className="h-full z-10"
          center={[mapCentreLong, mapCentreLat]}
          zoom={8}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            referrerPolicy="origin"
          />
          <LayerGroup ref={markerRef}>{markers}</LayerGroup>

          <LayerGroup>{polylines}</LayerGroup>
        </MapContainer>
      )}
    </div>
  );
}
